import { Lock, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import type { SubscriptionFeature } from '../types';
import { cn } from '@/lib/utils';

interface LockedFeatureCardProps {
  feature: SubscriptionFeature;
  requiredPlan: string;
  title?: string;
  description?: string;
  onUpgrade?: () => void;
  className?: string;
}

export function LockedFeatureCard({
  feature,
  requiredPlan,
  title,
  description,
  onUpgrade,
  className,
}: LockedFeatureCardProps) {
  const { t } = useTranslation();

  const featureTitle = title ?? t(`subscription.features.${feature}`);
  const featureDescription =
    description ?? t('subscription.upgradeToUnlock', { plan: requiredPlan });

  return (
    <Card
      className={cn(
        'relative overflow-hidden border-dashed bg-muted/30',
        className
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-muted/20 pointer-events-none" />
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="flex size-10 items-center justify-center rounded-full bg-muted">
            <Lock className="size-5 text-muted-foreground" />
          </div>
          <div>
            <CardTitle className="text-base">{featureTitle}</CardTitle>
            <CardDescription>{featureDescription}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {t('subscription.featureLockedDescription', {
            feature: featureTitle,
            plan: requiredPlan,
          })}
        </p>
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" onClick={onUpgrade}>
          {t('subscription.viewPlans')}
          <ArrowUpRight className="size-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
